export const FRONTEND_DIAGNOSTIC_EVENT = 'frontend-diagnostic';

/** Cap diagnostic text so large schema/render dumps cannot flood logs. */
export const DIAGNOSTIC_MESSAGE_MAX_LENGTH = 500;
export const DIAGNOSTIC_STACK_MAX_LENGTH = 2000;

export type IntegrationCatalogComingSoonDetail = {
  type: 'integration_catalog_coming_soon';
  integration_id: string;
  integration_name: string;
  href?: string;
  timestamp: string;
  context?: Record<string, unknown>;
};

export type ReactBoundaryErrorDetail = {
  type: 'react_boundary';
  boundary: string;
  message: string;
  stack?: string;
  componentStack?: string | null;
  pathname?: string;
  timestamp: string;
};

export type ReportReactBoundaryErrorArgs = {
  boundary: string;
  error: Error;
  componentStack?: string | null;
};

// ============================================================
// Private methods
// ============================================================

/** Redact query/hash parameter values that often carry tokens or signed URLs. */
function _redactUrlSecrets(text: string): string {
  return text
    .replace(/([?&][^=\s]+=)([^&#\s]*)/g, '$1[REDACTED]')
    .replace(/(#)([^?\s]*)/g, '$1[REDACTED]');
}

function _truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength)}…`;
}

// ============================================================
// Public methods
// ============================================================

/**
 * Safe page location for diagnostics: origin + pathname only (no query/hash).
 *
 * Example: `https://app.ept.ai/new-password?token=abc` -> `https://app.ept.ai/new-password`
 */
export function sanitizeDiagnosticHref(
  href: string | undefined = typeof window !== 'undefined' ? window.location.href : undefined
): string | undefined {
  if (!href) {
    return undefined;
  }
  try {
    const url = new URL(href, typeof window !== 'undefined' ? window.location.origin : undefined);
    return `${url.origin}${url.pathname}`;
  } catch {
    return _truncate(_redactUrlSecrets(href).split(/[?#]/)[0] || href, 300);
  }
}

/**
 * Scrub and truncate diagnostic text (messages, stacks) before logging/egress.
 *
 * Example: `fail at /x?token=secret` -> `fail at /x?token=[REDACTED]`
 */
export function sanitizeDiagnosticText(
  text: string | null | undefined,
  maxLength: number = DIAGNOSTIC_MESSAGE_MAX_LENGTH
): string | undefined {
  if (text == null || text === '') {
    return undefined;
  }
  return _truncate(_redactUrlSecrets(String(text)), maxLength);
}

/** Log a coming-soon integration request via custom event + console. */
export function reportIntegrationCatalogComingSoon(
  integrationId: string,
  integrationName: string,
  context?: Record<string, unknown>
): void {
  const detail: IntegrationCatalogComingSoonDetail = {
    type: 'integration_catalog_coming_soon',
    integration_id: integrationId,
    integration_name: integrationName,
    href: sanitizeDiagnosticHref(),
    timestamp: new Date().toISOString(),
    context,
  };

  console.warn('[integration-catalog] coming soon requested', detail);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FRONTEND_DIAGNOSTIC_EVENT, { detail }));
  }
}

/**
 * Report a React error-boundary catch to ErrorReporter via custom event.
 *
 * Example:
 * ```ts
 * reportReactBoundaryError({
 *   boundary: 'route',
 *   error: new Error('boom'),
 *   componentStack: errorInfo.componentStack,
 * });
 * ```
 */
export function reportReactBoundaryError(args: ReportReactBoundaryErrorArgs): void {
  const detail: ReactBoundaryErrorDetail = {
    type: 'react_boundary',
    boundary: args.boundary,
    message:
      sanitizeDiagnosticText(
        args.error.message || args.error.name || 'Unknown boundary error',
        DIAGNOSTIC_MESSAGE_MAX_LENGTH
      ) || 'Unknown boundary error',
    stack: sanitizeDiagnosticText(args.error.stack, DIAGNOSTIC_STACK_MAX_LENGTH),
    componentStack: sanitizeDiagnosticText(
      args.componentStack,
      DIAGNOSTIC_STACK_MAX_LENGTH
    ),
    pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
    timestamp: new Date().toISOString(),
  };

  if (process.env.NODE_ENV !== 'production') {
    console.error('[react-boundary]', detail);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FRONTEND_DIAGNOSTIC_EVENT, { detail }));
  }
}
